import { TRANSLATIONS } from '@pperform/pp-constants/translations';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ptpLanguageI18n } from 'i18n';
import { initDateTimeLanguage } from 'services/locales.services';

interface UseLocalizationProps {
  /**
   * Language code for i18n translations (e.g., 'en', 'es', 'fr', 'de')
   * Use to load translation strings for the application UI
   */
  language: string;
}

interface UseLocalizationReturn {
  /**
   * Locale code derived from language (e.g., 'en-gb', 'es-es', 'fr-fr')
   * Used by moment.js and datetime libraries to format dates, times, and numbers
   */
  locale: string;
  isInitializingLocale: boolean;
}

/**
 * Hook to initialize i18n translations and datetime locale settings
 *
 * @param props - Hook properties
 * @param props.language - Language code for i18n translations (e.g., 'en', 'es', 'fr')
 * @returns Object containing locale and initialization status
 *
 * @example
 * const { locale, isInitializingLocale } = useLocalization({
 *   language: 'en'
 * });
 */
export const useLocalization = ({
  language,
}: UseLocalizationProps): UseLocalizationReturn => {
  // Derive locale from language using TRANSLATIONS mapping
  const locale = useMemo(() => {
    if (!language) return 'en-gb';
    return TRANSLATIONS[language]?.momentLocale || 'en-gb';
  }, [language]);
  const [isInitializingLocale, setInitializingLocale] = useState(false);

  useEffect(() => {
    if (!language || !locale) {
      return;
    }

    moment.locale(locale);

    const initialize = async () => {
      try {
        // Wait for i18n instance to be initialized
        if (!ptpLanguageI18n.isInitialized) {
          await new Promise((resolve) => {
            ptpLanguageI18n.on('initialized', () => resolve(true));
          });
        }

        // Pre-load translations into i18n store
        await ptpLanguageI18n.changeLanguage(language);

        // Initialize datetime language
        if (locale !== 'en-gb') {
          await initDateTimeLanguage(locale);
        }
      } catch (error) {
        console.error('Error during init language:', error);
      } finally {
        setInitializingLocale(true);
      }
    };

    initialize();
  }, [language, locale]);

  return {
    locale,
    isInitializingLocale,
  };
};
